import {
  initializeFaro,
  getWebInstrumentations,
  ConsoleInstrumentation,
} from '@grafana/faro-web-sdk'

/**
 * Grafana Faro Web SDK — frontend telemetry to the observability stack.
 *
 * Pipes to Alloy (`/observability/faro/collect` — same origin, proxied
 * by nginx to `alloy:12347`), which forwards to Loki. Captured signals:
 *  - Unhandled errors (window.onerror + unhandledrejection)
 *  - Console warnings + errors (level ≥ `warn`)
 *  - Web vitals (LCP, FID, CLS, FCP, TTFB) as performance entries
 *  - Page views + session metadata
 *  - Optional manual logs / metrics via `faro.api.pushLog()` / `pushMeasurement()`
 *
 * What we DO NOT enable (heavyweight; revisit when there's a need):
 *  - Session replay — significant bundle + bandwidth cost
 *  - Tracing — separate concern; lands when OTel + Tempo/Jaeger arrive
 *
 * Gracefully degrades:
 *  - If `VITE_FARO_DISABLED=true` → no SDK init at all (zero overhead).
 *  - If Alloy is down / nginx returns non-2xx → SDK retries with
 *    backoff internally, never throws to app code.
 */
export function initObservability(): void {
  if (import.meta.env.VITE_FARO_DISABLED === 'true') {
    return
  }

  // Faro reads the SPA host from `window.location.origin` — same-origin
  // proxy to Alloy keeps cookie/auth/CORS irrelevant.
  const url = `${window.location.origin}/observability/faro/collect`

  initializeFaro({
    url,
    app: {
      name: 'lockerserver-fe',
      version: import.meta.env.VITE_APP_VERSION ?? 'dev',
      environment: import.meta.env.MODE,
    },
    instrumentations: [
      // Errors, web vitals, sessions, page views, navigation, performance,
      // user actions, CSP violations.
      ...getWebInstrumentations(),
      // Console capture is a SEPARATE instrumentation in @grafana/faro-web-sdk
      // v2+. SDK v2.6.x ships it level-filter-free; consumers calling
      // `console.warn` / `console.error` are the realistic level
      // emitters anyway (we don't `console.log` for debug-only output
      // — that's `import.meta.env.DEV`-gated).
      new ConsoleInstrumentation(),
    ],
    // Keep batches tight on dev so the path can be verified quickly.
    // Faro's transport batches internally; this just sets the upper bound.
    batching: {
      enabled: true,
      sendTimeout: 250,
      itemLimit: 50,
    },
  })
}
